import { BreadcrumbItem } from "@/components/Breadcrumb";
import { PageHeader } from "@/components/PageHeader";
import { Helmet } from "react-helmet-async";
import { StatsOverview } from "@/components/Dashboard/StatsOverview";
import { MemberStatsTabs } from "@/components/Dashboard/MemberStatsTabs";
import { NewRegistrationsChart } from "@/components/Dashboard/NewRegistrationsChart";
import { MemberDistributionChart } from "@/components/Dashboard/MemberDistributionChart";
import { RecentMembersList } from "@/components/Dashboard/RecentMembersList";
import { RecentCommissionsList } from "@/components/Dashboard/RecentCommissionsList";
import {
  mockStats,
  StatCardData,
} from "@/data/mockDashboardData";
import { useEffect, useState } from "react";
import { DashboardGeneralAPI, DashboardMemberCardAPI, DashboardMemberChartAPI, DashboardPackageDistributionAPI, DashboardRecentActivityAPI } from "@/api/type";
import { getDashboardGeneral, getDashboardMemberCard, getDashboardMemberChart, getDashboardPackageDistribution, getDashboardRecentActivity } from "@/api/Dashboard";
import { useTranslation } from "react-i18next";

export default function HomePage() {
  const { t } = useTranslation();
  const [dashboardStats, setDashboardStats] = useState<StatCardData[] | undefined>();
  const [dashboardPackageDistribution, setDashboardPackageDistribution] = useState<DashboardPackageDistributionAPI | undefined>();
  const [dashboardRecentActivity, setDashboardRecentActivity] = useState<DashboardRecentActivityAPI | undefined>();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t('home.title'), path: '/' },
    { label: t('home.breadcrumb_all') },
  ];

  useEffect(() => {
    // Fetch dashboard data from API and set state
    getDashboardGeneral().then(response => {
      if (response.status && response.data) {
        const stats: StatCardData[] = [
          {
            label: t('home.total_investment'),
            value: `đ ${Number(response.data.total_investment.value).toLocaleString('vi-VN', { maximumFractionDigits: 0 })}`,
            trend: {
              percentage: Math.abs(response.data.total_investment.percent),
              isPositive: response.data.total_investment.percent >= 0,
              text: t('home.compared_to_last_month')
            },
            icon: 'CircleDollarSign',
            chartData: response.data.total_investment.chart || []
          },
          {
            label: t('home.total_revenue'),
            value: `đ ${Number(response.data.total_revenue.value).toLocaleString('vi-VN', { maximumFractionDigits: 0 })}`,
            trend: {
              percentage: Math.abs(response.data.total_revenue.percent),
              isPositive: response.data.total_revenue.percent >= 0,
              text: t('home.compared_to_last_month')
            },
            icon: 'TrendingUp',
            chartData: response.data.total_revenue.chart || []
          },
          {
            label: t('home.total_members'),
            value: response.data.total_members.value.toLocaleString('vi-VN', { maximumFractionDigits: 0 }),
            trend: {
              percentage: Math.abs(response.data.total_members.percent),
              isPositive: response.data.total_members.percent >= 0,
              text: t('home.compared_to_last_month'),
            },
            icon: 'Users',
            chartData: response.data.total_members.chart || []
          },
          {
            label: t('home.active_packages'),
            value: response.data.active_packages.value.toLocaleString('vi-VN', { maximumFractionDigits: 0 }),
            trend: {
              percentage: Math.abs(response.data.active_packages.percent),
              isPositive: response.data.active_packages.percent >= 0,
              text: t('home.compared_to_last_month')
            },
            icon: 'Package',
            chartData: response.data.active_packages.chart || []
          }
        ];
        setDashboardStats(stats);
      }
    }).catch(() => { });
    getDashboardPackageDistribution().then(data => setDashboardPackageDistribution(data)).catch(() => { });
    getDashboardRecentActivity().then(data => setDashboardRecentActivity(data)).catch(() => { });
  }, [t]);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>{t('home.page_title')}</title>
      </Helmet>
      <PageHeader title={t('home.title')} breadcrumbs={breadcrumbs} />
      <StatsOverview stats={dashboardStats || mockStats} />
      <MemberStatsTabs />
      <NewRegistrationsChart/>
      <MemberDistributionChart distributionData={dashboardPackageDistribution} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentMembersList members={dashboardRecentActivity?.data?.recent_referrals || []} />
        <RecentCommissionsList commissions={dashboardRecentActivity?.data?.recent_commissions || []} />
      </div>
    </div>
  );
}